import { useEffect, useState } from 'react';

interface DiagResult {
  label: string;
  ok: boolean;
  detail: string;
}

export default function MediaDiag() {
  const [results, setResults] = useState<DiagResult[]>([]);
  const [deviceList, setDeviceList] = useState<MediaDeviceInfo[]>([]);
  const [secureCtx, setSecureCtx] = useState<string>('?');
  const [location, setLocation] = useState<string>('?');
  const [userAgent, setUserAgent] = useState<string>('?');

  const add = (r: DiagResult) => setResults((arr) => [...arr, r]);

  useEffect(() => {
    setSecureCtx(window.isSecureContext ? 'YES ✅' : 'NO ❌(getUserMedia 不允许!)');
    setLocation(window.location.href);
    setUserAgent(navigator.userAgent);

    (async () => {
      // 1. navigator.mediaDevices exists
      if (!navigator.mediaDevices) {
        add({ label: 'navigator.mediaDevices', ok: false, detail: '对象不存在(非安全上下文或非常老的浏览器)' });
        return;
      }
      add({ label: 'navigator.mediaDevices', ok: true, detail: 'exists' });

      // 2. getUserMedia exists
      if (!navigator.mediaDevices.getUserMedia) {
        add({ label: 'getUserMedia()', ok: false, detail: '方法不存在' });
        return;
      }
      add({ label: 'getUserMedia()', ok: true, detail: 'exists' });

      // 3. enumerateDevices
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        setDeviceList(devs);
        const cams = devs.filter((d) => d.kind === 'videoinput');
        const mics = devs.filter((d) => d.kind === 'audioinput');
        add({
          label: 'enumerateDevices()',
          ok: cams.length + mics.length > 0,
          detail: `总设备 ${devs.length}(摄像头 ${cams.length} / 麦克风 ${mics.length} / 扬声器 ${devs.filter(d => d.kind === 'audiooutput').length})`,
        });
      } catch (e: any) {
        add({ label: 'enumerateDevices()', ok: false, detail: `${e.name}: ${e.message}` });
      }

      // 4. audio only
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        add({ label: 'getUserMedia({audio:true})', ok: true, detail: `OK,track=${s.getAudioTracks().map(t => t.label).join('|') || '(空 label)'}` });
        s.getTracks().forEach((t) => t.stop());
      } catch (e: any) {
        add({ label: 'getUserMedia({audio:true})', ok: false, detail: `${e.name}: ${e.message || '(无 message)'}` });
      }

      // 5. video only
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: true });
        add({ label: 'getUserMedia({video:true})', ok: true, detail: `OK,track=${s.getVideoTracks().map(t => t.label).join('|') || '(空 label)'}` });
        s.getTracks().forEach((t) => t.stop());
      } catch (e: any) {
        add({ label: 'getUserMedia({video:true})', ok: false, detail: `${e.name}: ${e.message || '(无 message)'}` });
      }

      // 6. both
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        add({ label: 'getUserMedia({audio:true, video:true})', ok: true, detail: `OK,两路 track` });
        s.getTracks().forEach((t) => t.stop());
      } catch (e: any) {
        add({ label: 'getUserMedia({audio:true, video:true})', ok: false, detail: `${e.name}: ${e.message || '(无 message)'}` });
      }
    })();
  }, []);

  return (
    <div className="container">
      <h1>媒体设备诊断</h1>
      <div className="card">
        <p><b>URL:</b> <code>{location}</code></p>
        <p><b>isSecureContext:</b> {secureCtx}</p>
        <p><b>User-Agent:</b> <code style={{ fontSize: 11 }}>{userAgent}</code></p>
      </div>
      <div className="card">
        <h2>检查项</h2>
        <table>
          <thead><tr><th style={{ width: 40 }}></th><th>项</th><th>详情</th></tr></thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i}>
                <td>{r.ok ? '✅' : '❌'}</td>
                <td>{r.label}</td>
                <td style={{ fontSize: 12, wordBreak: 'break-all' }}>{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>enumerateDevices 完整列表</h2>
        {deviceList.length === 0 && <p className="hint">(空 —— 系统没检测到设备或 API 没返回)</p>}
        {deviceList.length > 0 && (
          <table>
            <thead><tr><th>kind</th><th>label</th><th>deviceId(前 12 字符)</th></tr></thead>
            <tbody>
              {deviceList.map((d) => (
                <tr key={d.deviceId}>
                  <td>{d.kind}</td>
                  <td>{d.label || <i style={{ color: '#f59e0b' }}>(空 label = 浏览器未获授权)</i>}</td>
                  <td style={{ fontSize: 11 }}>{d.deviceId.slice(0, 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
